import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Sparkles, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { formatCurrency, formatPercent } from '@/lib/format';
import { KpiCard } from '@/components/KpiCard';
import { PublishGateBanner } from '@/components/publish/PublishGateBanner';
import type { PublishGateInfo } from '@/lib/types-publish-gate';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';
import { useScopePinStore } from '@/stores/scope-pin';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTh } from '@/components/SortableTh';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';

/** `/dashboard` (edition='lite' 時、v1.0 Phase 3 Sprint 11)。
 *  設計: docs/design/v10-multi-tenant.md Chapter 4 (Lite版データモデル) / 4.7 (Full版との共存)。
 *  Full版DashboardOverviewPage.tsxとは対象テーブルが異なるため独立ページ化 (routeレベルでの分岐)。
 *  多店舗テナント(90店舗超)向けUX改善 (検索/ソート/ランキング/master-detail 2カラム/前月比較)。 */

type Kpis = {
  revenue: number; cogs: number; labor: number; rent: number; utility: number; opex: number;
  manualAdjustments: number; operatingProfit: number;
  foodCostRatio: number | null; laborCostRatio: number | null; flCostRatio: number | null;
  rentRatio: number | null; operatingProfitRatio: number | null;
};
type ComparisonRow = Kpis & { node_id: string; node_name: string };
type ComparisonResponse = { month: string; stores: ComparisonRow[]; publish_gate?: PublishGateInfo | null };
type SummaryResponse = {
  node_id: string; node_name: string; month: string;
  /** §8-151: 公開ゲートで未公開の月は null (0 埋めにしない — 「売上0円」と誤読されるため) */
  kpis: Kpis | null;
  publish_gate?: PublishGateInfo | null;
  prev_month: { month: string; kpis: Kpis } | null;
  category_lines: Array<{ name: string; category_type: string; amount: number }>;
  supplier_lines: Array<{ name: string; amount: number }>;
};
type TrendResponse = {
  node_id: string;
  /** §8-151: 未公開の月は値が null (系列から落とさず線を途切れさせて欠測を伝える) */
  series: Array<{ month: string; revenue: number | null; cogs: number | null; labor: number | null; operating_profit: number | null }>;
  publish_gate?: PublishGateInfo | null;
};
type UsedInstruction = { instruction_id: string; title: string };
type CommentResponse = { comment_id: string; status: 'pending' | 'generating' | 'succeeded' | 'failed'; comment_body: string | null; error_message: string | null; generated_at: string | null; used_instructions?: UsedInstruction[] } | null;

/** v0.3.x AIコメント カスタム指示 (docs/design/v03-ai-comment-custom-instructions.md §8-2)。
 *  GET /ai-instructions は tenant_features がOFF、または ai_instruction:view 未保有だと403になるため、
 *  取得できたかどうか自体を「この画面で指示を選べるか」の判定に使う (フラグ確認用の
 *  GET /tenant-features は company_admin 限定で店長が読めないため、こちらで代替する)。 */
type InstructionsResponse = {
  selection_mode: 'tenant' | 'node';
  max_instructions: number;
  instructions: Array<{ instruction_id: string; title: string; body: string; is_default_enabled: boolean }>;
};

const buildMonthOptions = (): string[] => {
  const opts: string[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
  }
  return opts;
};

export function LiteDashboardPage() {
  const qc = useQueryClient();
  // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-5): 月軸はFull版と同じscope-pinストアを
  // 参照する。ノード軸はNodeSwitcher/組織ツリーを持たないため、/nodesを呼ばず
  // useScopeStore.selectedNodeId/setSelectedNodeIdだけを直接参照・呼び出す形で接続する
  // (Full版と同じ「選択のたびに自動永続化」の恩恵を新規storeを増やさず展開)。
  // ⚠️ localStorageからの復元はNodeSwitcher.tsxの/nodes取得(setTree)経由の副作用に依存している
  // (NodeSwitcher.tsxのearly returnより前にuseQuery/setTreeがある実装順序を変更しないこと)。
  const tenantId = useAuthStore((s) => s.user?.tenant_id ?? null);
  const globalSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const setGlobalSelectedNodeId = useScopeStore((s) => s.setSelectedNodeId);
  const isPinned = useScopePinStore((s) => s.isPinned);
  const pinnedPeriodEnd = useScopePinStore((s) => s.pinnedPeriodEnd);
  const monthOptions = buildMonthOptions();
  if (isPinned && pinnedPeriodEnd && !monthOptions.includes(pinnedPeriodEnd)) {
    monthOptions.push(pinnedPeriodEnd);
    monthOptions.sort().reverse();
  }
  const [month, setMonth] = useState(() => (isPinned && pinnedPeriodEnd ? pinnedPeriodEnd : monthOptions[0]!));
  useEffect(() => {
    if (isPinned && pinnedPeriodEnd) setMonth(pinnedPeriodEnd);
  }, [isPinned, pinnedPeriodEnd]);
  const [searchText, setSearchText] = useState('');
  const detailRef = useRef<HTMLDivElement | null>(null);

  const { data: comparison, isLoading: comparisonLoading } = useQuery<ComparisonResponse>({
    queryKey: ['lite-dashboard', 'comparison', month],
    queryFn: () => apiFetch<ComparisonResponse>(`/lite-dashboard/comparison?month=${month}`),
  });

  const activeNodeId = globalSelectedNodeId ?? comparison?.stores[0]?.node_id ?? null;

  const { data: summary, isLoading: summaryLoading } = useQuery<SummaryResponse>({
    queryKey: ['lite-dashboard', 'summary', activeNodeId, month],
    queryFn: () => apiFetch<SummaryResponse>(`/lite-dashboard/summary?node_id=${activeNodeId}&month=${month}`),
    enabled: !!activeNodeId,
  });

  const { data: trend } = useQuery<TrendResponse>({
    queryKey: ['lite-dashboard', 'trend', activeNodeId, month],
    queryFn: () => apiFetch<TrendResponse>(`/lite-dashboard/trend?node_id=${activeNodeId}&months=6&month=${month}`),
    enabled: !!activeNodeId,
  });

  const { data: comment } = useQuery<CommentResponse>({
    queryKey: ['lite-dashboard', 'comment', activeNodeId, month],
    queryFn: () => apiFetch<CommentResponse>(`/lite-dashboard/comment?node_id=${activeNodeId}&month=${month}`),
    enabled: !!activeNodeId,
  });

  // v0.3.x AIコメント カスタム指示: 機能OFF/権限無しなら403で data が undefined のままになり、選択UIは出ない。
  // QA M-2: フラグOFFテナントでは毎回403になるが、フラグ状態を読む GET /tenant-features は
  // company_admin 限定で店長が読めないため、この「叩いてみる」方式を意図的に採る。
  // リトライ・フォーカス再取得を止め、gcTime を長めに取って往復を1セッション1回程度に抑える。
  const { data: instructionSettings } = useQuery<InstructionsResponse>({
    queryKey: ['ai-instructions'],
    queryFn: () => apiFetch<InstructionsResponse>('/ai-instructions'),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  const canPickInstructions = instructionSettings?.selection_mode === 'tenant'
    && (instructionSettings?.instructions.length ?? 0) > 0;
  /** null = 未選択 (テナント既定を使う)。Set = この回だけの明示指定 (空Setなら指示なしで生成)。 */
  const [pickedInstructionIds, setPickedInstructionIds] = useState<Set<string> | null>(null);
  const [instructionPickerOpen, setInstructionPickerOpen] = useState(false);
  // Testレビュー #5 / QA LOW: 店舗・月を切り替えても選択が残り、別店舗の生成に前店舗の選択が
  // 適用されてしまう (ハッシュも変わる)。対象が変わったら既定に戻す。
  const instructionPickScopeRef = useRef<string | null>(null);
  const currentPickScope = `${activeNodeId ?? ''}|${month}`;
  if (instructionPickScopeRef.current !== currentPickScope) {
    instructionPickScopeRef.current = currentPickScope;
    if (pickedInstructionIds !== null) setPickedInstructionIds(null);
  }
  const defaultInstructionIds = useMemo(
    () => (instructionSettings?.instructions ?? []).filter((i) => i.is_default_enabled).map((i) => i.instruction_id),
    [instructionSettings],
  );
  const effectivePickCount = pickedInstructionIds?.size ?? defaultInstructionIds.length;

  const generateCommentMut = useMutation({
    mutationFn: () => apiFetch<CommentResponse>('/lite-dashboard/comment', {
      method: 'POST',
      body: JSON.stringify({
        node_id: activeNodeId,
        month,
        ...(canPickInstructions && pickedInstructionIds ? { instruction_ids: [...pickedInstructionIds] } : {}),
      }),
    }),
    onSuccess: (data) => {
      qc.setQueryData(['lite-dashboard', 'comment', activeNodeId, month], data);
      toast.success('AIコメントを生成しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'AIコメントの生成に失敗しました'),
  });

  const kpis = summary?.kpis;
  const prevKpis = summary?.prev_month?.kpis ?? null;

  // 「どこが一番売れているか」に直接答えるため、現在の並び替え・検索状態とは独立に
  // 売上降順の順位を固定で保持する (ソート列を変えても1〜3位バッジの意味がぶれないようにする)
  const revenueRankByNode = useMemo(() => {
    const sorted = [...(comparison?.stores ?? [])].sort((a, b) => b.revenue - a.revenue);
    const map = new Map<string, number>();
    sorted.forEach((s, i) => map.set(s.node_id, i + 1));
    return map;
  }, [comparison?.stores]);

  const filteredStores = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const stores = comparison?.stores ?? [];
    return q ? stores.filter((s) => s.node_name.toLowerCase().includes(q)) : stores;
  }, [comparison?.stores, searchText]);

  const { sortKey, sortDir, toggleSort, sortedRows } = useTableSort(filteredStores, {
    defaultKey: 'revenue',
    defaultDir: 'desc',
  });

  const handleSelectStore = (nodeId: string) => {
    if (tenantId) setGlobalSelectedNodeId(tenantId, nodeId);
    // 1024px未満(一覧と詳細が縦積みのレイアウト)でのみ、選択直後に詳細セクションへスクロールする
    // (1024px以上はmaster-detailの2カラムで両方が常に見えているため不要。Designレビュー指摘: 768px
    // 起点だと768〜1023pxで詳細パネルが狭すぎて破綻するためlg(1024px)起点に変更)
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) {
      requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-h2">ダッシュボード</h2>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((m) => <SelectItem key={m} value={m}>{m.slice(0, 7)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="lg:flex lg:items-start lg:gap-4">
        {/* 左ペイン(1024px以上ではsticky): 店舗一覧。検索・ソート・売上ランキングバッジ付き。
            768〜1023pxは詳細パネルが狭くなりすぎるため、2カラム化はlg(1024px)起点にしている
            (Designレビュー指摘: md起点だと768px時点で詳細パネル幅が約60pxまで潰れKpiCardが破綻する) */}
        <div className="border border-neutral-line rounded-lg bg-white overflow-hidden lg:w-[420px] xl:w-[520px] lg:shrink-0 lg:sticky lg:top-2 lg:self-start">
          <div className="p-3 border-b border-neutral-line flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[160px]">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-sub pointer-events-none" aria-hidden />
              <label htmlFor="lite-store-search" className="sr-only">店舗名で検索</label>
              <input id="lite-store-search" type="text" value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="店舗名で検索"
                className="w-full pl-6 pr-2 py-1.5 border border-neutral-line rounded text-sm focus:outline-none focus-ring" />
            </div>
            <span className="text-xs text-neutral-sub tabular-nums whitespace-nowrap">
              {sortedRows.length}/{comparison?.stores.length ?? 0}店舗
            </span>
          </div>
          <div className="overflow-x-auto lg:max-h-[calc(100vh-160px)] lg:overflow-y-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-neutral-panel text-neutral-sub lg:sticky lg:top-0 lg:z-10">
                <tr>
                  <SortableTh sortKey="node_name" currentKey={sortKey} dir={sortDir} onClick={toggleSort}>店舗</SortableTh>
                  <SortableTh sortKey="revenue" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">売上</SortableTh>
                  <SortableTh sortKey="foodCostRatio" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">原価率</SortableTh>
                  <SortableTh sortKey="laborCostRatio" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">人件費率</SortableTh>
                  <SortableTh sortKey="flCostRatio" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">FL比率</SortableTh>
                  <SortableTh sortKey="operatingProfit" currentKey={sortKey} dir={sortDir} onClick={toggleSort} align="right">営業利益</SortableTh>
                </tr>
              </thead>
              <tbody>
                {comparisonLoading && <tr><td colSpan={6} className="px-4 py-6 text-center text-neutral-sub">読み込み中…</td></tr>}
                {!comparisonLoading && sortedRows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-neutral-sub">該当する店舗がありません</td></tr>
                )}
                {sortedRows.map((s) => {
                  const rank = revenueRankByNode.get(s.node_id);
                  const isRanked = rank != null && rank <= 3;
                  return (
                    <tr key={s.node_id}
                      onClick={() => handleSelectStore(s.node_id)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectStore(s.node_id); }
                      }}
                      className={`border-t border-neutral-line cursor-pointer hover:bg-surface-sunken focus-ring ${activeNodeId === s.node_id ? 'bg-surface-sunken' : ''}`}>
                      <td className="px-3 py-2 font-medium">
                        <span className="flex items-center gap-1.5 min-w-0">
                          {isRanked && (
                            <span
                              className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full bg-misespark-navy text-white text-[10px] font-bold"
                              title={`売上 ${rank}位`}>
                              {rank}
                            </span>
                          )}
                          <span className="truncate">{s.node_name}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(s.revenue)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.foodCostRatio === null ? '−' : formatPercent(s.foodCostRatio / 100)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.laborCostRatio === null ? '−' : formatPercent(s.laborCostRatio / 100)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.flCostRatio === null ? '−' : formatPercent(s.flCostRatio / 100)}</td>
                      <td className={`px-3 py-2 text-right font-medium tabular-nums whitespace-nowrap ${s.operatingProfit < 0 ? 'text-danger-text' : 'text-success-text'}`}>
                        {/* WCAG 1.4.1対策: 黒字/赤字を色だけでなく▲/▼記号でも表現 (KpiCardと同じ表現方式)。
                            符号は▲/▼に一本化し、formatCurrencyの負符号("¥-500")との二重表示を避ける */}
                        <span aria-hidden>{s.operatingProfit < 0 ? '▼' : '▲'}</span> {formatCurrency(Math.abs(s.operatingProfit))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 右ペイン: 選択中店舗の詳細。1024px以上は左の一覧と横並びで常に両方が見える */}
        <div className="mt-6 lg:mt-0 flex-1 min-w-0">
          {!activeNodeId ? (
            <div className="text-sm text-neutral-sub border border-dashed border-neutral-line rounded-lg p-8 text-center">
              左の一覧から店舗を選択してください
            </div>
          ) : (
            <div ref={detailRef} className="space-y-4 scroll-mt-4">
              <h3 className="text-h3">{summary?.node_name ?? ''} の詳細</h3>

              {/* 既存DashboardOverviewPage.tsxと同じ列数上限 (2/3列) — 6列だと大きな負の金額が
                  truncateされて視認できなくなる (KpiCardのtruncate+titleフォールバックはあるが
                  「金額は省略しない」原則の趣旨に反するため、列を絞って回避する) */}
              {/* §8-151: 公開ゲートのバナー (Full 版と同じ共通コンポーネント) */}
              <PublishGateBanner publishGate={summary?.publish_gate} />

              {!summaryLoading && summary && !kpis && (
                <div className="text-sm text-neutral-sub border border-dashed border-neutral-line rounded-lg p-8 text-center">
                  この期間はまだ公開されていないため、実績を表示できません。
                </div>
              )}

              {summaryLoading ? <div className="text-neutral-sub">読み込み中…</div> : kpis && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  <KpiCard title="売上" value={kpis.revenue} format="currency"
                    delta={prevKpis ? { prevValue: prevKpis.revenue, label: '前月比' } : null} />
                  <KpiCard title="原価率" value={kpis.foodCostRatio === null ? null : kpis.foodCostRatio / 100} format="percent"
                    delta={prevKpis ? { prevValue: prevKpis.foodCostRatio === null ? null : prevKpis.foodCostRatio / 100, label: '前月比', invert: true } : null} />
                  <KpiCard title="人件費率" value={kpis.laborCostRatio === null ? null : kpis.laborCostRatio / 100} format="percent"
                    delta={prevKpis ? { prevValue: prevKpis.laborCostRatio === null ? null : prevKpis.laborCostRatio / 100, label: '前月比', invert: true } : null} />
                  <KpiCard title="FL比率" value={kpis.flCostRatio === null ? null : kpis.flCostRatio / 100} format="percent"
                    delta={prevKpis ? { prevValue: prevKpis.flCostRatio === null ? null : prevKpis.flCostRatio / 100, label: '前月比', invert: true } : null} />
                  <KpiCard title="家賃比率" value={kpis.rentRatio === null ? null : kpis.rentRatio / 100} format="percent"
                    delta={prevKpis ? { prevValue: prevKpis.rentRatio === null ? null : prevKpis.rentRatio / 100, label: '前月比', invert: true } : null} />
                  <KpiCard title="営業利益" value={kpis.operatingProfit} format="currency"
                    delta={prevKpis ? { prevValue: prevKpis.operatingProfit, label: '前月比' } : null} />
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-neutral-line rounded-lg bg-white p-4">
                  <h4 className="font-semibold text-sm mb-3">勘定科目別内訳</h4>
                  {(summary?.category_lines.length ?? 0) === 0
                    ? <p className="text-sm text-neutral-sub">データがありません</p>
                    : (
                      <table className="w-full text-sm">
                        <tbody>
                          {summary!.category_lines.map((l, i) => (
                            <tr key={i} className="border-t border-neutral-line first:border-t-0">
                              <td className="py-1.5">{l.name}</td>
                              <td className="py-1.5 text-right">{formatCurrency(l.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                </div>
                <div className="border border-neutral-line rounded-lg bg-white p-4">
                  <h4 className="font-semibold text-sm mb-3">仕入先別コスト</h4>
                  {(summary?.supplier_lines.length ?? 0) === 0
                    ? <p className="text-sm text-neutral-sub">データがありません</p>
                    : (
                      <table className="w-full text-sm">
                        <tbody>
                          {summary!.supplier_lines.map((l, i) => (
                            <tr key={i} className="border-t border-neutral-line first:border-t-0">
                              <td className="py-1.5">{l.name}</td>
                              <td className="py-1.5 text-right">{formatCurrency(l.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                </div>
              </div>

              {trend && trend.series.length > 0 && (
                <div className="border border-neutral-line rounded-lg bg-white p-4">
                  <h4 className="font-semibold text-sm mb-3">推移 (直近6ヶ月)</h4>
                  <div style={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer>
                      <LineChart data={trend.series}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tickFormatter={(m: string) => m.slice(0, 7)} fontSize={12} />
                        <YAxis tickFormatter={(v: number) => formatCurrency(v)} fontSize={12} width={80} />
                        <Tooltip formatter={(v: number) => formatCurrency(v)} labelFormatter={(m: string) => m.slice(0, 7)} />
                        {/* Designレビュー指摘: brand-red(#C4262E)はSortableThの選択列強調色と同時に
                            画面へ出ると「赤は1画面1箇所」原則に抵触するため、chart.2に変更 */}
                        <Line type="monotone" dataKey="revenue" name="売上" stroke="#0B2450" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="operating_profit" name="営業利益" stroke="#3E5C8C" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className="border border-neutral-line rounded-lg bg-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <Sparkles size={16} className="text-brand-orange" /> AIコメント
                  </div>
                  {/* Design H-8: ボタンが2つ並ぶと320px幅で溢れるため折り返しを許可する */}
                  <div className="flex items-center gap-3 flex-wrap gap-y-1">
                    {canPickInstructions && (
                      <button type="button" onClick={() => {
                        setPickedInstructionIds(pickedInstructionIds ?? new Set(defaultInstructionIds));
                        setInstructionPickerOpen(true);
                      }}
                        className="flex items-center gap-1 text-xs text-neutral-sub hover:text-neutral-text min-h-[44px] whitespace-nowrap">
                        {/* Design H-9: 生成前に何が使われるかを画面に出す */}
                        <SlidersHorizontal size={12} /> 使う指示を選ぶ ({effectivePickCount}件)
                      </button>
                    )}
                    <button type="button" onClick={() => generateCommentMut.mutate()} disabled={generateCommentMut.isPending}
                      className="flex items-center gap-1 text-xs text-neutral-sub hover:text-neutral-text disabled:opacity-50 min-h-[44px] whitespace-nowrap">
                      <RotateCcw size={12} /> {generateCommentMut.isPending ? '生成中…' : (comment ? '再生成' : '生成する')}
                    </button>
                  </div>
                </div>
                {!comment && !generateCommentMut.isPending && <p className="text-sm text-neutral-sub">まだコメントがありません。「生成する」を押してください</p>}
                {comment?.status === 'succeeded' && <p className="text-sm whitespace-pre-wrap">{comment.comment_body}</p>}
                {comment?.status === 'failed' && <p className="text-sm text-danger-text">生成に失敗しました: {comment.error_message}</p>}
                {comment?.status === 'succeeded' && (comment.used_instructions?.length ?? 0) > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    <span className="text-[11px] text-neutral-sub">使用した指示:</span>
                    {comment.used_instructions!.map((ins) => (
                      <span key={ins.instruction_id} className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border break-words">
                        {ins.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* v0.3.x AIコメント カスタム指示: この回だけ使う指示を選び直す */}
      <Modal
        open={instructionPickerOpen}
        title="このコメントで使う指示"
        size="sm"
        onClose={() => setInstructionPickerOpen(false)}
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => { setPickedInstructionIds(null); setInstructionPickerOpen(false); }}>
              既定に戻す
            </Button>
            <Button variant="primary" onClick={() => setInstructionPickerOpen(false)}>決定</Button>
          </div>
        )}
      >
        <div className="space-y-2">
          <p className="text-xs text-neutral-sub">
            チェックした指示だけがこのコメントの生成に使われます (すべて外すとカスタム指示なしで生成されます)。
            決定後に「生成する / 再生成」を押してください。
          </p>
          <ul className="space-y-1">
            {(instructionSettings?.instructions ?? []).map((ins) => (
              <li key={ins.instruction_id}>
                <label className="flex items-start gap-2 text-sm min-h-[44px] py-1">
                  <input
                    type="checkbox"
                    className="mt-1 w-5 h-5"
                    checked={pickedInstructionIds?.has(ins.instruction_id) ?? ins.is_default_enabled}
                    onChange={(e) => setPickedInstructionIds((prev) => {
                      const next = new Set(prev ?? defaultInstructionIds);
                      if (e.target.checked) next.add(ins.instruction_id); else next.delete(ins.instruction_id);
                      return next;
                    })}
                  />
                  <span className="min-w-0">
                    <span className="block break-words">{ins.title}</span>
                    {/* Design C-1: 本文は最大1000文字。全文表示するとモーダルが縦に伸びきるため2行に抑える */}
                    <span className="block text-xs text-neutral-sub break-words line-clamp-2">{ins.body}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </div>
  );
}
